import tenantData from '@data';
import tenantUtils from '@utils';
import tenantConstants from '@constants';
import { t } from 'i18next';
import tenantTheme from '@theme';
import store from '@store';
import { capitalizeFirstLetter } from '../../../utility/utility';

export const postedToBayut = (e) => {
  switch (e) {
    case 'not_listed':
      return false;
    case undefined:
      return false;
    case 'limit':
      return false;
    default:
      return true;
  }
};

const getSummaryData = (data, statuses) => {
  const arr = Object.keys(data)?.length > 0 ? Object.keys(data) : [];
  const stats = {};
  arr.forEach((e) => {
    const status = statuses?.find((it) => it.slug == e);
    stats[capitalizeFirstLetter(e)] = {
      title: status?.name,
      mapping: status?.id,
      total: data[e],
    };
  });
  return stats;
};

export const platformMapper = (e, user, setStatsLoading = true, existingStats) => {
  return {
    id: e.id,
    property_id: e?.id,
    slug: 'ksa',
    posted_on: e?.platforms?.posted_at,
    expiry_date: e?.expiry_date,
    updated_at: e?.updated_at,
    created_at: e?.created_at,
    renewing_on: e?.platforms?.['ksa']?.products_information?.['basic-listing']?.['auto_renewable_item']?.renewing_on,
    removed_at: e?.removed_at,
    ...(setStatsLoading && { views: 'loading', clicks: 'loading', leads: 'loading' }),
    status: tenantUtils.listingStatusMapper(e?.status?.slug),
    ...(!setStatsLoading && existingStats && existingStats),
    disposition: tenantUtils.listingDispositionMapper(e?.disposition, e),
    statusKey: 'disposition',
    ...(e.listing_purpose && { purpose: e.listing_purpose.title, purposeId: e?.listing_purpose?.id }),
    price: { value: e?.price, currency: t(tenantConstants.CURRENCY) },
    location: { ...e.location, breadcrumb: e?.location?.breadcrumb },
    posted: e?.status ? postedToBayut('active') : false,
    icon: 'IconBayut',
    public_url: tenantUtils.getLocalisedString(e, 'url'),
    listingPlatformActions: tenantUtils.getListingPlatformActions(e, 'ksa', user),
    listingRowActions: (refsObject, loading) => {
      const actions = tenantUtils.getListingRowActions(e, 'ksa', refsObject, loading);
      return actions;
    },
    expiryDays: e?.expiry_days || 30,
    ...(!!e?.user && { listingOwner: e?.user }),
    trucheck: e?.trucheck,
    is_otp_required: e?.is_otp_required,
    phone_number: e?.phone_number,
  };
};

export const listingMapper = (listing, user, setStatsLoading, existingStats = {}) => {
  const platforms = {
    property_id: listing?.id,
    ...(listing?.listing_purpose && { purpose: listing?.listing_purpose }),
    type: listing?.listing_type,
    price: { value: listing?.price, currency: tenantConstants.CURRENCY },
    location: { ...listing?.location, breadcrumb: listing?.location?.breadcrumb },
    auto_renewable_item: {
      isApplied: listing?.is_autorenewal_on,
      ...listing?.platforms?.['ksa']?.products_information?.['basic-listing']?.['auto_renewable_item'], //Pending
    },
    data: [
      ...(!!user?.products?.platforms['ksa'] ? [platformMapper(listing, user, setStatsLoading, existingStats)] : []),
    ],
  };
  return {
    ...listing,
    id: listing?.id,
    type: listing?.listing_type?.title,
    purpose: listing?.listing_purpose,
    property: {
      id: listing?.id,
      ...(listing?.listing_purpose && { purpose: listing?.listing_purpose }),
      isDailyRental: listing?.listing_purpose?.slug == 'daily-rental',
      isOffPlan: listing?.is_offplan_listing || null,
      projectStatus: listing?.completion_status === 'ready' ? 'Ready' : 'Off-Plan',
      booked: listing?.booked,
      bookedDates: listing?.additional_details?.booked_dates,
      saleType: listing?.sale_type,
      type: listing?.listing_type,
      area: { value: listing?.area_unit?.value, unit: listing?.area_unit?.name },
      location: {
        ...listing?.location,
        breadcrumb: listing?.location?.breadcrumb
          ?.filter((e) => e?.level > 1)
          ?.map((e) => tenantUtils.getLocalisedString(e, 'title'))
          ?.join(', '),
      },
      image: listing?.image,
      imageCount: listing?.images_count,
      price: { value: listing.price, currency: tenantConstants.CURRENCY },
      details: {
        ...listing?.rega_details?.property_specs,
        listingSpecs: tenantData?.getListingSpecs(listing),
        listing_type: listing?.listing_type,
        regaId: listing?.ad_license,
        regaExpiryDate: listing?.platforms?.rega_expiry_date,
        permit_number: listing?.permit_number,
      },
      ...(listing?.health && {
        health: listing?.health,
      }),
      showHealth: true,
      productsInfo: listing?.platforms?.ksa?.products_information, //Pending
      isBadge: listing?.platforms?.ksa?.products_information?.['basic-listing']?.is_applied,
      ...(listing?.discount_applied !== undefined && {
        discount_applied: listing.discount_applied,
      }),
      ...(listing?.discount_percentage != null && listing?.discount_percentage !== '' && {
        discount_percentage: listing.discount_percentage,
      }),
        ...(listing?.discounted_price != null && listing?.discounted_price !== '' && {
          discounted_price: listing.discounted_price,
      }),
      ...(listing?.actual_price != null &&
        listing?.actual_price !== '' && {
          actual_price: listing.actual_price,
        }),
      platforms,
    },
    price: { value: listing.price, currency: tenantConstants.CURRENCY },
    area: { value: listing?.area_unit?.value, unit: listing?.area_unit?.id || 2 },
    ...(listing?.health && {
      health: listing?.health,
    }),
    platforms,
  };
};

export const getMyListingsData = (summaryRes, listingsRes) => {
  const listings = listingsRes?.listing;
  const pagination = listingsRes?.pagination;
  const summary = getSummaryData(summaryRes?.summary, listingsRes?.statuses_and_dispositions);
  const user = store.getState().app.loginUser.user;

  return {
    list: listings && listings?.length ? listings?.map((item) => listingMapper(item, user, false)) : [],
    pagination: tenantUtils.getPaginationObject(pagination),
    tabFilterKey: 'q[status_id_eq]',
    dontAllowRowSlection: true,
    statuses_and_dispositions: listingsRes?.statuses_and_dispositions,
    statuses: summary
      ? [
          ...Object.keys(summary).map((e) => ({
            ...summary[e],
            key: e === 'All' ? '' : summary[e].mapping,
            label: `${t(capitalizeFirstLetter(e))} (${summary[e].total})`,
            tab: `${t(capitalizeFirstLetter(e))} (${summary[e].total})`,
          })),
        ]
      : [],
  };
};

const updateListingStats = (listing) => {
  if (listing) {
    return {
      views: listing?.sum_search_count,
      leads: listing?.sum_lead_count,
      clicks: listing?.sum_view_count,
      ctr: (listing?.sum_view_count / listing?.sum_search_count) * 100,
      calls: listing?.sum_phone_view_count,
      emails_clicked: listing?.sum_email_view_count,
      emails_received: listing?.sum_email_lead_count,
      sms: listing?.sum_sms_view_count,
      whatsapp: listing?.sum_whatsapp_view_count,
      chat: listing?.sum_chat_lead_count,
      stats: {
        views: listing?.sum_search_count,
        clicks: listing?.sum_view_count,
        leads: listing?.sum_lead_count,
      },
      whatsapp_sent: listing?.sum_whatsapp_lead_count,
      whatsapp_chats: listing?.chats_initiated,
      calls_received: listing?.received_calls,
      calls_answered: listing?.answered_calls,
      calls_missed: listing?.missed_calls,
    };
  } else {
    return {
      views: '',
      clicks: '',
      ctr: '',
      calls: '',
      emails: '',
      sms: '',
      whatsapp: '',
      leads: '',
      statsError: 'Bayut Stats Error',
    };
  }
};

const getProductDetailToBeApplied = (allProducts, productSlug) => {
  const details = allProducts?.find((it) => it?.slug == productSlug);

  if (details) {
    return { ...tenantData.getListingActions(productSlug), ...details };
  } else {
    return details;
  }
};

const upsellDataMapper = (platformWiseData, listingDetailResponse) => {
  const productsHash = {};
  tenantData.products.forEach((e) => {
    productsHash[e.slug] = e;
  });

  const data = {
    availableCredits: platformWiseData?.available_credits,
    creditUnitPrice: platformWiseData?.credit_unit_price,
    isPosted: listingDetailResponse?.isListingPosted,
    // isPosted: true,
    headingMsg: '',
    descriptionMsg: '',
    currency: '',
    applicableProducts: [],
    subServices: [],
    serviceStates: {},
    autoRenewState: {},
    listing: listingDetailResponse?.listing,
  };

  data?.availableCredits > 0 ? (data.currency = 'Credits') : (data.currency = tenantConstants.CURRENCY_SYMBOL());

  platformWiseData?.products.forEach((item) => {
    if (item.is_add_on === false && item.slug !== 'refresh') {
      data.applicableProducts.push({
        ...productsHash[item.slug],
        requiredCredits: item.credits_required,
        price: item.product_price,
        isRecommended: item.is_recommended,
        isAddOn: item.is_add_on,
        activeDuration: listingDetailResponse?.listingExpiryDays || 30,
        id: item.product_id,
        title: tenantUtils.getLocalisedString(item, 'product_title'),
        description: tenantUtils.getLocalisedString(item, 'description'),
      });
      data.autoRenewState[item.product_id] = false;
    } else if (item.is_add_on === true) {
      data.subServices.push({
        ...productsHash[item.slug],
        requiredCredits: item.credits_required,
        price: item.product_price,
        isRecommended: item.is_recommended,
        activeDuration: listingDetailResponse?.listingExpiryDays || 30,
        id: item.product_id,
        title: tenantUtils.getLocalisedString(item, 'product_title'),
        description: tenantUtils.getLocalisedString(item, 'description'),
      });
      data.serviceStates[item.product_id] = false;
    }
  });
  data.headingMsg = listingDetailResponse?.isListingPosted
    ? 'Your Listing has been Posted Successfully!'
    : 'Almost There';
  data.descriptionMsg = listingDetailResponse?.isListingPosted
    ? 'Make your Listing Hot and reach 10x more Buyers'
    : 'Choose the type of listing you want to publish';

  return data;
};

export const dataMapper = (platformWiseData, listingDetailResponse) => {
  const productsHash = {};
  tenantData.products.forEach((e) => {
    productsHash[e.slug] = e;
  });

  const data = {
    availableCredits: platformWiseData?.available_credits,
    creditUnitPrice: platformWiseData?.credit_unit_price,
    isPosted: listingDetailResponse?.isListingPosted,
    // isPosted: true,
    headingMsg: '',
    descriptionMsg: '',
    currency: '',
    applicableProducts: [],
    subServices: [],
    serviceStates: {},
    autoRenewState: {},
    listing: listingDetailResponse?.listing,
  };

  data?.availableCredits > 0 ? (data.currency = 'Credits') : (data.currency = tenantConstants.CURRENCY_SYMBOL());

  platformWiseData?.products.forEach((item) => {
    if (item.is_add_on === false && item.slug !== 'refresh') {
      data.applicableProducts.push({
        ...productsHash[item.slug],
        requiredCredits: item.credits_required,
        price: item.product_price,
        isRecommended: item.is_recommended,
        isAddOn: item.is_add_on,
        activeDuration: listingDetailResponse?.listingExpiryDays || 30,
        id: item.product_id,
        title: tenantUtils.getLocalisedString(item, 'product_title'),
        description: tenantUtils.getLocalisedString(item, 'description'),
      });
      data.autoRenewState[item.product_id] = false;
    } else if (item.is_add_on === true) {
      data.subServices.push({
        ...productsHash[item.slug],
        requiredCredits: item.credits_required,
        price: item.product_price,
        isRecommended: item.is_recommended,
        activeDuration: listingDetailResponse?.listingExpiryDays || 30,
        id: item.product_id,
        title: tenantUtils.getLocalisedString(item, 'product_title'),
        description: tenantUtils.getLocalisedString(item, 'description'),
      });
      data.serviceStates[item.product_id] = false;
    }
  });
  data.headingMsg = listingDetailResponse?.isListingPosted
    ? 'Your Listing has been Posted Successfully!'
    : 'Almost There';
  data.descriptionMsg = listingDetailResponse?.isListingPosted
    ? 'Make your Listing Hot and reach 10x more Buyers'
    : 'Choose the type of listing you want to publish';

  return data;
};

const widgetParser = (listings, platform) => {
  const user = store.getState().app?.loginUser.user;
  const productsToShow = user?.isCurrencyUser
    ? [{ slug: 'signature-listing' }, { slug: 'hot-listing' }, { slug: 'basic-listing' }]
    : [{ slug: 'hot-listing' }, { slug: 'basic-listing' }];

  const data = {
    ksa: {
      title: 'Listings',
      icon_data: {},
      link_data: {
        text: 'View All Listings',
        to: '/listings',
      },
      total_title: 'Active',
      total_title_icon: 'ActiveListingIcon',
      total_value: '',
      purposes: [
        {
          id: 1,
          icon: 'IconForSale',
          iconProps: { color: tenantTheme['color-for-sale'], hasBackground: true },
          title: 'For Sale',
          value: '',
        },
        {
          id: 2,
          icon: 'IconForRent',
          iconProps: { color: tenantTheme['color-for-rent'], hasBackground: true },
          title: 'To Rent',
          value: '',
        },
        {
          id: 4,
          icon: 'IconRental',
          iconProps: { color: tenantTheme['color-for-rent'], hasBackground: true },
          title: 'Daily Rentals',
          value: '',
        },
      ],
      products: tenantUtils.generateProductData(productsToShow),
    },
  };

  const currentPlatform = data?.[platform];
  currentPlatform.total_value = listings?.active;
  currentPlatform.purposes[0].value = listings?.sale;
  currentPlatform.purposes[1].value = listings?.rent;
  currentPlatform.purposes[2].value = listings?.daily_rental;
  currentPlatform.products.forEach((item, i) => {
    currentPlatform.products[i].value = listings?.[item.slug_alt];
  });

  return data?.[platform];
};

const getListingSummaryStats = (response, platform) => {
  return { [platform]: widgetParser(response?.data?.stats?.[platform], platform) };
};


export default {
  getMyListingsData,
  listingMapper,
  platformMapper,
  updateListingStats,
  getProductDetailToBeApplied,
  upsellDataMapper,
  getListingSummaryStats,
};
